import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DangerButton, PrimaryButton, SecondaryButton } from '../../../components/buttons';
import { EditColumns } from '../../../components/edit-columns';
import { HintTooltip } from '../../../components/hint-tooltip';
import { Loading, ThinLoadingBar } from '../../../components/loading';
import { Modal } from '../../../components/modal';
import { DohFilter } from '../../../components/product-table/filters/doh-filter';
import { LabelFilter } from '../../../components/product-table/filters/label-filter';
import { SizeFilter } from '../../../components/product-table/filters/size-filter';
import { TypeFilter } from '../../../components/product-table/filters/type-filter';
import { DisplayQuantityToggle } from '../../../components/quantity-toggle';
import { defaultProductSearchPlaceholders, SearchInput } from '../../../components/search-input';
import { SegmentedToggle } from '../../../components/segmented-toggle';
import { SortPopover } from '../../../components/sort-popover';
import { TanStackTableCSVButton } from '../../../components/tanstack-table/csv-button';
import type { AppRow } from '../../../components/tanstack-table/features';
import { TanStackTable } from '../../../components/tanstack-table/table';
import { useResponsiveColumnSizing } from '../../../components/tanstack-table/use-column-sizing';
import { useRouteRowSelection } from '../../../components/tanstack-table/use-route-row-selection';
import { useTableRef } from '../../../components/tanstack-table/use-table-ref';
import { PageToolbar } from '../../../components/toolbar/page-toolbar';
import { ToolbarPopover } from '../../../components/toolbar/popover';
import { SearchWithCount } from '../../../components/toolbar/search-with-count';
import { ProductTableContext } from '../../../contexts/product-table-context';
import { isPivotEligible, type Product } from '../../../data/product';
import { hasSwingDeals } from '../../../data/states';
import { useKeyboardShortcut } from '../../../helpers/use-keyboard-shortcut';
import { useOrders } from '../../../hooks/orders';
import { useProducts } from '../../../hooks/products';
import { useEngagement } from '../../../hooks/use-engagement';
import { useScrollPreservation } from '../../../hooks/use-scroll-preservation';
import { useSearchFocus } from '../../../hooks/use-search-focus';
import { useVendor, useVendorIntegrations } from '../../../hooks/vendors';
import { orderEditRoute, storeLayout } from '../../../router';
import { AddProductModal } from './components/add-product-modal';
import { DealsFamilyView } from './components/deals-family-view';
import { LastRefreshed } from './components/last-refreshed';
import { BuyupSummaryHeader, OrderSummaryHeader } from './components/order-summary-header';
import { useReorder } from './components/reorder-column';
import { ReorderSettingsPopover } from './components/reorder-settings-popover';
import { useReviewOrder } from './components/review-order';
import { ShowFilter } from './components/show-filter';
import { itemHasCount, type ProductOrderItem } from './data/product-order-item';
import { usePivotGlowStore } from './hooks/pivot-glow-store';
import {
  BUYUP_COLUMNS_KEY,
  DEFAULT_HIDDEN_COLUMNS,
  ORDER_COLUMNS_KEY,
  PINNED_COLUMNS,
  useColumns,
} from './hooks/use-columns';
import type { Family } from './hooks/use-families';
import { useFilteredOrder } from './hooks/use-filtered-order';
import { useOrderItems } from './hooks/use-order-items';
import { useScrollToProduct } from './hooks/use-scroll-to-product';
import { SORT_COLUMN_OPTIONS, useSortedOrder } from './hooks/use-sorted-order';
import type { EditOrderPopulatedProperties } from './types';
import { matchesIsBuyup, vendorHasVolumePricing } from './utils';

export function EditOrder() {
  const navigate = useNavigate();
  const { store } = storeLayout.useRouteContext();
  const { orderId } = orderEditRoute.useParams();
  const { orders } = useOrders();
  const order = useMemo(() => orders.find((order) => order.id === orderId), [orders, orderId]);
  const { vendor } = useVendor(order?.vendor_id);
  const { products } = useProducts();
  const vendorProducts = useMemo(() => {
    if (!products || !vendor) {
      return [];
    }

    return products.filter((product) => product.vendor_id === vendor.vendor_id);
  }, [products, vendor]);

  const canEdit =
    order !== undefined &&
    order.store_id === store.id &&
    vendor !== undefined &&
    products.length > 0;

  // Bounce to the order list when the order can't be edited — most often right
  // after cancelling it, which drops it from the orders cache while this route
  // is still mounted.
  //
  // This must fire exactly once. Every router state change re-renders us while
  // the navigation is in flight, and each new navigation cancels the pending
  // one, so a redirect that repeats on every render never settles: the route
  // stays mounted, re-renders, redirects again, and the page locks up. Hence
  // an effect with stable deps rather than <Navigate>, which re-navigates
  // whenever its props object identity changes — i.e. on every render.
  useEffect(() => {
    if (!canEdit) {
      navigate({ to: '/stores/$storeId/orders', params: { storeId: store.id }, replace: true });
    }
  }, [canEdit, navigate, store.id]);

  if (!canEdit) {
    return <Loading />;
  }

  return (
    <EditOrderPopulated order={order} vendor={vendor} products={vendorProducts} store={store} />
  );
}

function EditOrderPopulated(properties: EditOrderPopulatedProperties) {
  const [isBuyup, setIsBuyup] = useState(false);
  const prePivotSearchRef = useRef<string | null>(null);
  const [displayInCases, setDisplayInCases] = useState(false);
  const searchReference = useSearchFocus();

  const { pivotTo: pivotGlowTo, reset: resetPivotGlow, glowingId } = usePivotGlowStore();

  useEffect(() => {
    return () => {
      resetPivotGlow();
    };
  }, [resetPivotGlow]);

  const [addModalFamily, setAddModalFamily] = useState<Family | null>(null);

  const {
    order,
    orderItems,
    setOrderItem,
    joinProductToFamily,
    removeProductFromFamily,
    productSaving,
    calculating,
    resetOrder,
    showCancelConfirm,
    promptDeleteOrder,
    dismissCancelConfirm,
    confirmDeleteOrder,
    options,
    setOptions,
    lastRefreshed,
    paused,
  } = useOrderItems(properties);

  const triggerBuyupEngagement = useEngagement('buyup_engaged', properties.store.id);

  const wrappedSetOrderItem = useCallback(
    (item: ProductOrderItem) => {
      setOrderItem(item);
      if (isBuyup) {
        triggerBuyupEngagement({
          order_id: order.id,
          vendor_id: properties.vendor.vendor_id,
          vendor_normalized_name: properties.vendor.normalized_name,
        });
      }
    },
    [
      isBuyup,
      setOrderItem,
      triggerBuyupEngagement,
      order.id,
      properties.vendor.vendor_id,
      properties.vendor.normalized_name,
    ]
  );

  const reorderColumn = useReorder(options);

  const integrations = useVendorIntegrations();
  const vendorHasVolume = vendorHasVolumePricing(properties.vendor, integrations);
  const isVolumeDeals = isBuyup && vendorHasVolume;

  const {
    query,
    setQuery,
    filteredOrderItems,
    filterContext,
    orderProducts,
    showFilter,
    setShowFilter,
    activeFilterCount,
    clearFilters,
  } = useFilteredOrder(
    orderItems,
    isBuyup,
    // The family view renders disabled / no-recent-sales rows with badges, so
    // keep them in the working set instead of filtering them upstream.
    isVolumeDeals
  );
  const { sorting, sortPopoverSorting, handleSortPopoverChange } = useSortedOrder(
    reorderColumn.id,
    isBuyup
  );

  const hasBuyupData = useMemo(() => {
    return orderItems.some((orderItem) => matchesIsBuyup(orderItem.product, true));
  }, [orderItems]);

  const pivotToBuyup = useCallback(
    (product: Product) => {
      if (prePivotSearchRef.current === null) {
        prePivotSearchRef.current = query;
      }

      setQuery(product.brand);
      pivotGlowTo(product.id);
      setIsBuyup(true);

      requestAnimationFrame(() => {
        const input = searchReference.current;
        if (input) {
          input.focus();
          input.select();
        }
      });
    },
    [query, searchReference, setQuery, pivotGlowTo]
  );

  const pivotBrandWithinBuyup = useCallback(
    (product: Product) => {
      setQuery(product.brand);
      pivotGlowTo(product.id);

      requestAnimationFrame(() => {
        const input = searchReference.current;
        if (input) {
          input.focus();
          input.select();
        }
      });
    },
    [searchReference, setQuery, pivotGlowTo]
  );

  const setBuyupView = useCallback(
    (next: boolean) => {
      setIsBuyup((current) => {
        if (current && !next && prePivotSearchRef.current !== null) {
          setQuery(prePivotSearchRef.current);
          prePivotSearchRef.current = null;
        }

        return next;
      });
    },
    [setQuery]
  );

  useKeyboardShortcut('meta+b', () => {
    if (!hasBuyupData) {
      return;
    }

    setBuyupView(!isBuyup);
  });

  const showsSavingsIndicators = useMemo(() => {
    if (vendorHasVolume) {
      return orderItems.some((orderItem) => {
        return (
          (orderItem.product.pricing.previous_cost !== undefined &&
            orderItem.product.pricing.previous_volume_discounts &&
            orderItem.product.pricing.previous_volume_discounts.length > 0) ||
          (orderItem.product.pricing.current_cost !== undefined &&
            orderItem.product.pricing.volume_discounts &&
            orderItem.product.pricing.volume_discounts.length > 0) ||
          (orderItem.product.pricing.future_cost !== undefined &&
            orderItem.product.pricing.future_volume_discounts &&
            orderItem.product.pricing.future_volume_discounts.length > 0)
        );
      });
    }

    if (hasSwingDeals(properties.store)) {
      return orderItems.some((orderItem) => {
        return (
          orderItem.product.pricing.previous_cost !== undefined ||
          orderItem.product.pricing.current_cost !== undefined ||
          orderItem.product.pricing.future_cost !== undefined
        );
      });
    }

    return false;
  }, [orderItems, properties.store, vendorHasVolume]);

  const handleCasesSoldClick = useCallback(
    (product: Product, casesToOrder: number) => {
      if (!isBuyup) {
        return;
      }

      if (casesToOrder <= 0) {
        return;
      }

      const orderItem = orderItems.find((item) => item.product.id === product.id);
      if (orderItem) {
        wrappedSetOrderItem({
          ...orderItem,
          cases: casesToOrder,
          units: 0,
        });
      }
    },
    [isBuyup, orderItems, wrappedSetOrderItem]
  );

  // useColumns returns all columns (for TanStack sorting) + visibility state + visible columns (for layout)
  const { columns, visibleColumns, columnVisibility, columnOrder } = useColumns({
    store: properties.store,
    vendorHasVolume,
    setOrderItem: wrappedSetOrderItem,
    reorderColumn,
    isBuyup,
    showsSavingsIndicators,
    displayInCases,
    onCasesSoldClick: handleCasesSoldClick,
    onPivotToBuyup: pivotToBuyup,
    onBrandPivot: pivotBrandWithinBuyup,
  });

  const [showSubmitOrder, setShowSubmitOrder] = useState(false);
  const review = useReviewOrder({
    store: properties.store,
    vendor: properties.vendor,
    order,
    orderItems,
    methods: options.send_methods,
  });

  const toggleShowSubmitOrder = useCallback(() => {
    setShowSubmitOrder((show) => !show);
  }, []);

  const tableScrollRef = useRef<HTMLDivElement>(null);
  const scrollOffsetsStore = useRef<Record<string, number>>({});
  const { onScroll: onTableScroll } = useScrollPreservation(tableScrollRef, query !== '', {
    viewKey: isBuyup ? 'buyup' : 'regular',
    store: scrollOffsetsStore,
  });

  // Subscribes to the pivot-glow store and scrolls the flat-table row into
  // view when present. Family-view has its own virtualizer-aware variant; the
  // querySelector here returns null when family-view is rendered, so it's a
  // no-op without further gating.
  useScrollToProduct({ scrollRef: tableScrollRef });

  // Row selection for highlighting - separate selections for buyup vs regular views
  const {
    rowSelection,
    onRowSelectionChange,
    onRowClick: toggleRowSelection,
  } = useRouteRowSelection(isBuyup ? 'buyup' : 'regular');

  const handleRowClick = useCallback(
    (row: AppRow<ProductOrderItem>, event: React.MouseEvent) => {
      if (!isBuyup && (event.metaKey || event.ctrlKey)) {
        const product = row.original.product;
        if (isPivotEligible(product, vendorHasVolume)) {
          pivotToBuyup(product);

          return;
        }
      }

      toggleRowSelection(row);
    },
    [isBuyup, pivotToBuyup, toggleRowSelection, vendorHasVolume]
  );

  const { columnSizing } = useResponsiveColumnSizing(visibleColumns);

  // visibleColumns is in column-definition order, but the table header renders
  // in columnOrder (the user's drag order). The summary cells must follow the
  // same order or they drift out of alignment with the headers as soon as a
  // column is reordered.
  const orderedVisibleColumns = useMemo(() => {
    const byId = new Map(visibleColumns.map((col) => [col.id, col]));

    return columnOrder.flatMap((id) => {
      const col = byId.get(id);

      return col ? [col] : [];
    });
  }, [visibleColumns, columnOrder]);

  // Table ref for CSV export
  const [table, onTableReady] = useTableRef<ProductOrderItem>();

  // Non-ordering rows are still the primary scanning surface (the user is
  // reading them to decide whether to stock up), so don't dim them — just
  // shift the text to a slightly softer gray so committed (ordering) rows
  // read at full contrast by comparison.
  const getRowClassName = useCallback(
    (item: ProductOrderItem) => {
      const isGlowing = item.product.id === glowingId;
      const glowClass = isGlowing ? 'animate-pivot-glow' : '';

      return [itemHasCount(item) ? '' : 'text-gray-700!', glowClass].filter(Boolean).join(' ');
    },
    [glowingId]
  );

  // Ordering rows get a wider stripe (full lane) so they pop out of the
  // flow; non-ordering rows get a thinner stripe within the same reserved
  // lane so row width stays constant. The ordering stripe uses the same brand
  // emerald as the row-selection rail so the two greens read as one system.
  const getRowStripe = useCallback((item: ProductOrderItem) => {
    return itemHasCount(item)
      ? { color: 'var(--color-primary-700)', width: 6 }
      : { color: 'var(--color-gray-400)', width: 3 };
  }, []);

  // Header content for TanStackTable (uses visibleColumns for layout alignment)
  const headerContent = useMemo(() => {
    if (isBuyup) {
      return (
        <BuyupSummaryHeader
          items={filteredOrderItems}
          columns={orderedVisibleColumns}
          columnSizing={columnSizing}
        />
      );
    }

    return (
      <OrderSummaryHeader
        items={filteredOrderItems}
        columns={orderedVisibleColumns}
        columnSizing={columnSizing}
      />
    );
  }, [filteredOrderItems, orderedVisibleColumns, columnSizing, isBuyup]);

  const isSwing = hasSwingDeals(properties.store);

  // Block only while the initial seed is running. Don't also gate on
  // `orderItems.length === 0`: an order that legitimately has no items (e.g. a
  // draft whose vendor's products were all reassigned) would otherwise spin
  // forever, leaving the user unable to open — and therefore cancel — it.
  if (calculating) {
    return <Loading />;
  }

  return (
    <>
      <div className="grid grid-cols-3 items-center px-4 py-2">
        <div />
        <h1 className="text-2xl font-semibold justify-self-center">
          {properties.vendor.name} Order
        </h1>
        <LastRefreshed lastRefreshed={lastRefreshed} paused={paused} />
      </div>
      <PageToolbar
        left={
          <>
            <ReorderSettingsPopover
              options={options}
              setOptions={setOptions}
              storeId={properties.store.id}
              vendorId={properties.vendor.vendor_id}
            />
            <ProductTableContext.Provider value={filterContext}>
              <ToolbarPopover
                icon={<AdjustmentsHorizontalIcon className="size-5" />}
                label="Filters"
                badge={activeFilterCount}
                onClear={clearFilters}
              >
                <ShowFilter value={showFilter} onChange={setShowFilter} />
                <SizeFilter products={orderProducts} />
                <TypeFilter products={orderProducts} />
                <LabelFilter />
                <DohFilter />
              </ToolbarPopover>
            </ProductTableContext.Provider>
            {!isVolumeDeals && (
              <SortPopover
                sorting={sortPopoverSorting}
                onSortingChange={handleSortPopoverChange}
                sortColumnOptions={SORT_COLUMN_OPTIONS}
              />
            )}
            {!isVolumeDeals && (
              <EditColumns
                tableKey={isBuyup ? BUYUP_COLUMNS_KEY : ORDER_COLUMNS_KEY}
                pinnedColumns={PINNED_COLUMNS}
                defaultHiddenColumnIds={DEFAULT_HIDDEN_COLUMNS}
                sorting={sorting}
              />
            )}
          </>
        }
        right={
          <>
            {!isBuyup && (
              <DisplayQuantityToggle
                displayInCases={displayInCases}
                setDisplayInCases={setDisplayInCases}
              />
            )}
            {hasBuyupData && (
              <HintTooltip
                text={isSwing ? 'Toggle order/buyup view' : 'Toggle order/deal view'}
                shortcut="meta+b"
              >
                <div>
                  <SegmentedToggle
                    value={isBuyup ? 'buyup' : 'order'}
                    onChange={(value) => setBuyupView(value === 'buyup')}
                    options={[
                      { value: 'order', label: 'Order' },
                      { value: 'buyup', label: isSwing ? 'Buyup' : 'Deals' },
                    ]}
                  />
                </div>
              </HintTooltip>
            )}

            <SearchWithCount
              quantity={filteredOrderItems.length}
              total={orderItems.length}
              noun="items"
            >
              <SearchInput
                grouped
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  prePivotSearchRef.current = null;
                }}
                inputRef={searchReference}
                placeholders={defaultProductSearchPlaceholders}
                shortcut="meta+f"
              />
            </SearchWithCount>
            <DangerButton onClick={promptDeleteOrder}>Cancel</DangerButton>
            <SecondaryButton onClick={resetOrder}>Reset</SecondaryButton>
            <PrimaryButton onClick={toggleShowSubmitOrder}>Purchase Order</PrimaryButton>
            <TanStackTableCSVButton
              table={table}
              filename={`${properties.vendor.name}-${
                isBuyup ? 'buyup' : 'order'
              }-${new Date().toISOString().split('T')[0]}.csv`}
            />
          </>
        }
      />
      {isVolumeDeals ? (
        <>
          <DealsFamilyView
            orderItems={orderItems}
            searchQuery={query}
            setOrderItem={wrappedSetOrderItem}
            onAddProductClicked={setAddModalFamily}
            onRemoveMember={(item) => removeProductFromFamily(item.product)}
            scrollOffsetsStore={scrollOffsetsStore}
          />
          {addModalFamily && (
            <AddProductModal
              family={addModalFamily}
              onClose={() => setAddModalFamily(null)}
              onAdd={(product) => joinProductToFamily(product, addModalFamily.id)}
            />
          )}
        </>
      ) : (
        <TanStackTable
          data={filteredOrderItems}
          columns={columns}
          columnVisibility={columnVisibility}
          columnOrder={columnOrder}
          rowHeight={25}
          sorting={sorting}
          rowSelection={rowSelection}
          onRowSelectionChange={onRowSelectionChange}
          onRowClick={handleRowClick}
          getRowId={(item) => item.product.id}
          headerContent={headerContent}
          scrollRef={tableScrollRef}
          onScroll={onTableScroll}
          getRowClassName={getRowClassName}
          getRowStripe={getRowStripe}
          onTableReady={onTableReady}
        />
      )}
      {showSubmitOrder && (
        <Modal
          title="Purchase Order"
          visible
          onClose={toggleShowSubmitOrder}
          footer={review.footer}
        >
          {review.body}
        </Modal>
      )}
      <Modal
        title="Cancel Order"
        visible={showCancelConfirm}
        onClose={dismissCancelConfirm}
        size="sm"
        footer={
          <>
            <SecondaryButton onClick={dismissCancelConfirm}>Keep Order</SecondaryButton>
            <DangerButton onClick={confirmDeleteOrder}>Cancel Order</DangerButton>
          </>
        }
      >
        <p className="text-sm text-gray-600">
          Are you sure you want to cancel this order? This cannot be undone.
        </p>
      </Modal>
      {productSaving && <ThinLoadingBar />}
    </>
  );
}
